import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import { formatError } from '@shared/utils'

import useFormatMessage from '@hooks/useFormatMessage'
import { styles as dialogStyles } from '@components/Dialog/Dialog.utils'

export default function ErrorDialog({ open, onClose, error }) {
  const formatMessage = useFormatMessage()
  const { text } = formatError(error)
  const details = getErrorDetails(error)

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={onClose}
      scroll="body"
      slotProps={{ paper: { sx: dialogStyles.paper } }}
      aria-labelledby="error-dialog-title"
      aria-describedby="error-dialog-description"
      role="alertdialog"
    >
      <DialogTitle id="error-dialog-title">
        <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          {details.status && (
            <Chip
              label={details.status}
              color="error"
              size="small"
              variant="outlined"
              sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
            />
          )}
          {details.endpoint && (
            <Typography variant="body2" sx={styles.mono}>
              {details.endpoint}
            </Typography>
          )}
          {details.message && !details.endpoint && (
            <Typography variant="body2" sx={{ color: 'error.main' }}>
              {details.message}
            </Typography>
          )}
          {!details.status && !details.endpoint && !details.message && formatMessage('error.page.details.title')}
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }} id="error-dialog-description">
        {error?.honeybadgerUrl && (
          <Stack direction="row" spacing={1} sx={styles.honeybadger}>
            <Typography
              component="a"
              variant="body2"
              href={error.honeybadgerUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={styles.honeybadgerLink}
            >
              {error.honeybadgerUrl}
            </Typography>
          </Stack>
        )}
        <Box component="dl" sx={styles.detailList}>
          {details.type && <DetailRow label="Type" value={details.type} />}
          {details.statusText && <DetailRow label="Status" value={`${details.status} ${details.statusText}`} />}
          {details.endpoint && <DetailRow label="Endpoint" value={details.endpoint} mono />}
          {details.message && <DetailRow label="Message" value={details.message} />}
          {details.responseMessage && <DetailRow label="Response" value={details.responseMessage} />}
          <DetailRow label="Timestamp" value={new Date().toLocaleString()} />
        </Box>
        <Box sx={{ px: 3, py: 2 }}>
          <Typography component="pre" variant="body2" tabIndex={0} sx={styles.codeBlock}>
            {text}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={dialogStyles.actions}>
        <Button {...dialogStyles.button} onClick={onClose} variant="contained" aria-label={formatMessage('ui.dialog.a11y.close')}>
          {formatMessage('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function DetailRow({ label, value, mono }) {
  return (
    <Box sx={styles.detailRow}>
      <Typography component="dt" variant="caption" sx={{ minWidth: 90, color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography component="dd" variant="body2" sx={mono ? styles.mono : { wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Box>
  )
}

function getErrorDetails(error) {
  if (!error) return {}

  const status = error.response?.status
  const statusText = error.response?.statusText
  const method = error.config?.method?.toUpperCase()
  const url = error.config?.url
  const endpoint = method && url ? `${method} ${url}` : undefined

  const responseData = error.response?.data
  const responseMessage =
    responseData?.errors?.map((e) => (Array.isArray(e.detail) ? e.detail.join(', ') : e.detail)).join('; ') ||
    responseData?.error ||
    undefined

  let type
  if (error.isAxiosError) type = 'Network Error'
  else if (error.name) type = error.name
  else type = 'Error'

  return { type, status, statusText, endpoint, message: error.message, responseMessage }
}

const monoFont = '"Fira Code", "JetBrains Mono", Monaco, Menlo, "Ubuntu Mono", Consolas, source-code-pro, monospace'

const styles = {
  mono: {
    fontFamily: monoFont,
    wordBreak: 'break-all',
  },
  honeybadger: {
    alignItems: 'center',
    px: 3,
    py: 1.5,
    bgcolor: 'action.hover',
  },
  honeybadgerLink: {
    color: 'text.primary',
    wordBreak: 'break-all',
    '&:hover': { color: 'primary.main' },
  },
  detailList: {
    m: 0,
    px: 3,
    py: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
  },
  detailRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 2,
    m: 0,
  },
  codeBlock: {
    textAlign: 'left',
    fontFamily: monoFont,
    fontSize: '0.75rem',
    backgroundColor: 'background.default',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    p: 2,
    mt: 1,
    overflow: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
}
